import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, Mail, Menu, Phone, Search, X } from 'lucide-react';
import { Button } from '../components/Button.jsx';
import { SearchInputGroup } from '../components/SearchInputGroup.jsx';
import PublicContainer from './PublicContainer.jsx';

export const PUBLIC_HEADER_DEFAULT_SUPPORT = {
  email: 'support@mytownspotlight.com',
  phone: '(555) 555-0123',
};

export const PUBLIC_HEADER_DEFAULT_UTILITY_LINKS = [
  { label: 'Spotlight Blog', href: '/articles' },
  { label: 'Log In', href: '/login' },
  { label: 'Register', href: '/register' },
];

export const PUBLIC_HEADER_DEFAULT_CTA = {
  label: 'Claim Your Listing',
  href: '/claim/new',
};

export const PUBLIC_HEADER_SUBCOMPONENTS = [
  {
    id: 'top-bar-links',
    icon: Mail,
    title: 'Top bar links',
    description: 'Utility links and support contact actions that sit above the main header.',
    tokens: ['--primary', '--primary-foreground', '--primary-soft'],
    zone: { top: 0, left: 0, right: 0, height: 52 },
  },
  {
    id: 'brand-block',
    icon: Menu,
    title: 'Brand block',
    description: 'Tenant logo and title area that anchor the public navigation.',
    tokens: ['--surface', '--border'],
    zone: { top: 52, left: 0, width: 220, height: 64 },
  },
  {
    id: 'primary-navigation',
    icon: ChevronDown,
    title: 'Primary navigation',
    description: 'The main nav items rendered from shared public navigation definitions.',
    tokens: ['--primary', '--primary-soft', '--primary-hover', '--primary-foreground'],
    zone: { top: 52, left: 220, right: 220, height: 64 },
  },
  {
    id: 'search-trigger',
    icon: Search,
    title: 'Search trigger',
    description: 'The desktop search affordance for directory lookup.',
    tokens: ['--border', '--text', '--muted', '--primary', '--background'],
    zone: { top: 52, right: 174, width: 44, height: 64 },
  },
  {
    id: 'cta-button',
    icon: Phone,
    title: 'CTA button',
    description: 'The claim-listing call to action that drives conversion.',
    tokens: ['--primary', '--primary-foreground', '--primary-hover'],
    zone: { top: 52, right: 0, width: 174, height: 64 },
  },
  {
    id: 'mobile-controls',
    icon: X,
    title: 'Mobile controls',
    description: 'Collapsed menu states and mobile open/close interactions.',
    tokens: ['--menu-text-color', '--menu-hover-bg-color', '--menu-hover-text-color'],
    zone: { top: 52, right: 0, width: 88, height: 64 },
  },
];

function resolveLinkClick(event, href, onClick, onNavigate) {
  if (onClick) {
    event.preventDefault();
    onClick(event);
    return;
  }

  if (onNavigate && typeof href === 'string' && href.length > 0) {
    event.preventDefault();
    onNavigate(href);
  }
}

function formatPhoneHref(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
  return digits ? `tel:+${digits}` : '';
}

const PublicHeader = ({
  logo,
  navLinks,
  primaryCta,
  secondaryCta,
  variant = 'solid',
  brand,
  navItems = [],
  utilityLinks = [],
  support,
  cta,
  mobileUserAuth = null,
  className,
  showTopBar = true,
  sticky = true,
  onNavigate,
  ...props
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBusinessesMegaMenuOpen, setIsBusinessesMegaMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const businessesMegaMenuCloseTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const hasBrandVisual = Boolean((logo?.element || logo?.src) || (brand?.logo?.element || brand?.logo?.src));

  const cancelBusinessesMegaMenuClose = () => {
    if (businessesMegaMenuCloseTimeoutRef.current) {
      window.clearTimeout(businessesMegaMenuCloseTimeoutRef.current);
      businessesMegaMenuCloseTimeoutRef.current = null;
    }
  };

  const openBusinessesMegaMenu = () => {
    cancelBusinessesMegaMenuClose();
    setIsBusinessesMegaMenuOpen(true);
  };

  const closeBusinessesMegaMenu = () => {
    cancelBusinessesMegaMenuClose();
    setIsBusinessesMegaMenuOpen(false);
  };

  const scheduleCloseBusinessesMegaMenu = () => {
    cancelBusinessesMegaMenuClose();
    businessesMegaMenuCloseTimeoutRef.current = window.setTimeout(() => {
      businessesMegaMenuCloseTimeoutRef.current = null;
      setIsBusinessesMegaMenuOpen(false);
    }, 150);
  };

  useEffect(() => {
    if (isSearchOpen) {
      searchInputRef.current?.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => () => {
    cancelBusinessesMegaMenuClose();
  }, []);

  const handleSearchSubmit = (query) => {
    if (!query) return;
    onNavigate?.(`/directory?search=${encodeURIComponent(query)}`);
    setIsSearchOpen(false);
    setSearchValue('');
  };

  const resolvedBrand = (() => {
    if (logo?.element || logo?.src) {
      return { ...brand, logo };
    }
    if (React.isValidElement(logo)) {
      return { ...brand, logo: { element: logo } };
    }
    return brand;
  })();

  const resolvedNavItems = Array.isArray(navLinks) && navLinks.length > 0
    ? navLinks.map((item) => ({
        label: item.label,
        href: item.href,
        children: Array.isArray(item.children) ? item.children : [],
        active: Boolean(item.active),
        onClick: item.onClick,
      }))
    : navItems;

  const resolvedPrimaryCta = primaryCta
    ? {
        label: primaryCta.label,
        href: primaryCta.href,
        onClick: primaryCta.onClick,
      }
    : cta;

  const resolvedSecondaryCta = secondaryCta || null;
  const headerSurfaceClass = variant === 'transparent'
    ? 'border-b border-transparent bg-transparent shadow-none'
    : 'border-b border-[#E5E7EB] bg-white shadow-sm';

  const rootClassName = clsx(sticky ? 'sticky top-0 z-50' : '', className);
  return (
    <header className={rootClassName} {...props}>
      {showTopBar ? (
        <div className="h-[52px] bg-[var(--primary,#0F172A)]">
          <PublicContainer className="h-full">
            <div className="flex h-full items-center justify-between gap-4">
              <div className="hidden items-center gap-4 sm:flex">
                {support?.email ? (
                  <a
                    href={`mailto:${support.email}`}
                    className="flex items-center font-body text-[13px] text-[var(--primary-foreground,#FFFFFF)] transition-colors hover:text-[var(--primary-soft,#DBEAFE)] rounded px-2 py-1"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    {support.email.toUpperCase()}
                  </a>
                ) : null}
                {support?.phone ? <span className="text-[var(--primary-foreground,#FFFFFF)] opacity-50">/</span> : null}
                {support?.phone ? (
                  <a
                    href={support.phoneHref || formatPhoneHref(support.phone)}
                    className="flex items-center font-body text-[13px] text-[var(--primary-foreground,#FFFFFF)] transition-colors hover:text-[var(--primary-soft,#DBEAFE)] rounded px-2 py-1"
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    {support.phone}
                  </a>
                ) : null}
              </div>

              <div className="ml-auto flex items-center gap-1">
                {utilityLinks.map((link, index) => (
                  <React.Fragment key={link.label}>
                    <a
                      href={link.href || '#'}
                      className="inline-flex min-h-11 items-center rounded px-3 py-2 font-body text-[13px] text-[var(--primary-foreground,#FFFFFF)] transition-colors hover:text-[var(--primary-soft,#DBEAFE)]"
                      onClick={(event) => resolveLinkClick(event, link.href, link.onClick, onNavigate)}
                    >
                      {link.label}
                    </a>
                    {index < utilityLinks.length - 1 ? <span className="text-[var(--primary-foreground,#FFFFFF)] opacity-50">/</span> : null}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </PublicContainer>
        </div>
      ) : null}

      <div className={headerSurfaceClass}>
        <PublicContainer>
          <div className="relative flex h-16 items-center justify-between gap-4">
            <a
              href={resolvedBrand?.href || '/'}
              className="flex min-w-0 items-center gap-3"
              onClick={(event) => resolveLinkClick(event, resolvedBrand?.href, resolvedBrand?.onClick, onNavigate)}
            >
              {resolvedBrand?.logo?.element ? resolvedBrand.logo.element : null}

              {!resolvedBrand?.logo?.element && resolvedBrand?.logo?.src ? (
                <img
                  src={resolvedBrand.logo.src}
                  alt={resolvedBrand.logo.alt || `${resolvedBrand?.title || 'Brand'} logo`}
                  className="h-10 w-auto max-w-[220px] shrink-0 object-contain"
                  loading="eager"
                />
              ) : null}

              <span className="min-w-0">
                {!hasBrandVisual ? (
                  <span className="block truncate font-display text-[26px] font-bold leading-tight text-[#1E3A8A]">
                    {resolvedBrand?.title}
                  </span>
                ) : null}
              </span>
            </a>

            <div className="relative hidden lg:block">
              <nav className="flex items-center gap-2" aria-label="Primary">
                {resolvedNavItems.map((item) => {
                  const isBusinesses = String(item.label || '').toLowerCase() === 'businesses';
                  const childItems = Array.isArray(item.children) ? item.children.filter((child) => child?.label && child?.href) : [];

                  if (!isBusinesses && childItems.length > 0) {
                    return (
                      <div key={item.label} className="group relative">
                        <a
                          href={item.href || '#'}
                          className={clsx(
                            'inline-flex items-center overflow-hidden rounded-md border border-transparent font-body text-[15px] font-medium transition-colors',
                            item.active
                              ? 'bg-[var(--primary,#1D4ED8)] text-[var(--primary-foreground,#FFFFFF)] hover:text-[var(--primary-soft,#DBEAFE)]'
                              : 'text-[var(--primary,#1D4ED8)] hover:bg-[var(--primary-soft,#DBEAFE)] hover:text-[var(--primary-hover,#1E40AF)]'
                          )}
                          onClick={(event) => resolveLinkClick(event, item.href, item.onClick, onNavigate)}
                          aria-haspopup="true"
                        >
                          <span className="px-3 py-2">{item.label}</span>
                          <span className="border-l border-[#E2E8F0] px-2 py-2">
                            <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                          </span>
                        </a>

                        <div className="invisible absolute left-0 top-full z-[80] min-w-56 pt-2 opacity-0 transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                          <div className="rounded-xl border border-[#E2E8F0] bg-white p-2 shadow-xl">
                            {childItems.map((child) => (
                              <a
                                key={child.label}
                                href={child.href}
                                className="block rounded-lg px-3 py-2 font-body text-[14px] font-medium text-[#0F172A] transition hover:bg-[var(--primary-soft,#DBEAFE)] hover:text-[var(--primary-hover,#1E40AF)]"
                                onClick={(event) => resolveLinkClick(event, child.href, child.onClick, onNavigate)}
                              >
                                {child.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (!isBusinesses) {
                    return (
                      <a
                        key={item.label}
                        href={item.href || '#'}
                        className={clsx(
                          'rounded-md px-3 py-2 font-body text-[15px] font-medium transition-colors',
                          item.active
                            ? 'bg-[var(--primary,#1D4ED8)] text-[var(--primary-foreground,#FFFFFF)] hover:text-[var(--primary-soft,#DBEAFE)]'
                            : 'text-[var(--primary,#1D4ED8)] hover:bg-[var(--primary-soft,#DBEAFE)] hover:text-[var(--primary-hover,#1E40AF)]'
                        )}
                        onClick={(event) => resolveLinkClick(event, item.href, item.onClick, onNavigate)}
                      >
                        {item.label}
                      </a>
                    );
                  }

                  return (
                    <div
                      key={item.label}
                      className="relative"
                      onMouseEnter={openBusinessesMegaMenu}
                      onMouseLeave={scheduleCloseBusinessesMegaMenu}
                      onFocus={openBusinessesMegaMenu}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          closeBusinessesMegaMenu();
                        }
                      }}
                    >
                      <a
                        href={item.href || '#'}
                        className={clsx(
                          'inline-flex items-center overflow-hidden rounded-md border border-transparent font-body text-[15px] font-medium transition-colors',
                          item.active
                            ? 'bg-[var(--primary,#1D4ED8)] text-[var(--primary-foreground,#FFFFFF)] hover:text-[var(--primary-soft,#DBEAFE)]'
                            : 'text-[var(--primary,#1D4ED8)] hover:bg-[var(--primary-soft,#DBEAFE)] hover:text-[var(--primary-hover,#1E40AF)]'
                        )}
                        onClick={(event) => {
                          closeBusinessesMegaMenu();
                          resolveLinkClick(event, item.href, item.onClick, onNavigate);
                        }}
                        aria-haspopup="true"
                        aria-expanded={isBusinessesMegaMenuOpen}
                      >
                        <span className="px-3 py-2">{item.label}</span>
                        <span className="border-l border-[#E2E8F0] px-2 py-2">
                          <ChevronDown className={clsx('h-4 w-4 transition-transform', isBusinessesMegaMenuOpen ? 'rotate-180' : '')} />
                        </span>
                      </a>

                      {isBusinessesMegaMenuOpen ? (
                        <div
                          className="absolute left-0 top-full z-[80] w-[680px] pt-2"
                          onMouseEnter={openBusinessesMegaMenu}
                          onMouseLeave={scheduleCloseBusinessesMegaMenu}
                        >
                          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-2xl">
                            <div className="grid grid-cols-3 gap-4">
                              <button
                                type="button"
                                className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-left transition hover:border-[#BFDBFE] hover:bg-[#EFF6FF]"
                                onClick={() => {
                                  closeBusinessesMegaMenu();
                                  onNavigate?.('/directory');
                                }}
                              >
                                <p className="font-display text-[16px] font-semibold text-[#0F172A]">Browse Directory</p>
                                <p className="mt-1 text-[13px] text-[#64748B]">Explore all local businesses by category.</p>
                              </button>
                              <button
                                type="button"
                                className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-left transition hover:border-[#BFDBFE] hover:bg-[#EFF6FF]"
                                onClick={() => {
                                  closeBusinessesMegaMenu();
                                  onNavigate?.('/deals');
                                }}
                              >
                                <p className="font-display text-[16px] font-semibold text-[#0F172A]">Featured Deals</p>
                                <p className="mt-1 text-[13px] text-[#64748B]">Discover current offers from highlighted partners.</p>
                              </button>
                              <button
                                type="button"
                                className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-left transition hover:border-[#BFDBFE] hover:bg-[#EFF6FF]"
                                onClick={() => {
                                  closeBusinessesMegaMenu();
                                  onNavigate?.('/events');
                                }}
                              >
                                <p className="font-display text-[16px] font-semibold text-[#0F172A]">Events & Community</p>
                                <p className="mt-1 text-[13px] text-[#64748B]">Find events and neighborhood highlights.</p>
                              </button>
                            </div>
                            <div className="mt-4 rounded-xl bg-[#0F172A] px-4 py-3 text-[13px] text-[#CBD5E1]">
                              Placeholder mega menu content. Curated business modules can be added here next.
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </nav>

            </div>

            <div className="hidden shrink-0 items-center gap-3 lg:flex">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSearchSubmit(String(searchValue || '').trim());
                }}
                className={clsx(
                  'relative flex shrink-0 items-center transition-[width] duration-300 ease-out',
                  isSearchOpen ? 'w-64' : 'w-10'
                )}
              >
                <div
                  className={clsx(
                    'absolute inset-0 overflow-hidden transition-[opacity] duration-200 ease-out',
                    isSearchOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
                  )}
                >
                  <SearchInputGroup
                    ref={searchInputRef}
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    onSubmit={handleSearchSubmit}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        setIsSearchOpen(false);
                        setSearchValue('');
                      }
                    }}
                    onBlur={() => {
                      if (!searchValue.trim()) {
                        setIsSearchOpen(false);
                      }
                    }}
                    placeholder="Search businesses..."
                    className="w-full"
                    aria-label="Search businesses"
                  />
                </div>

                <button
                  type="button"
                  className={clsx(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--border,#CBD5E1)] bg-white text-[var(--primary,#1D4ED8)] transition-colors hover:border-[var(--primary,#1D4ED8)] hover:text-[var(--primary,#1D4ED8)]',
                    isSearchOpen ? 'pointer-events-none opacity-0' : 'opacity-100'
                  )}
                  onClick={() => {
                    if (!isSearchOpen) {
                      setIsSearchOpen(true);
                      return;
                    }

                    handleSearchSubmit(String(searchValue || '').trim());
                  }}
                  aria-label="Open business search"
                >
                  <Search className="h-4 w-4" />
                </button>
              </form>

              {resolvedSecondaryCta ? (
                <Button
                  variant="secondary"
                  className="w-[130px] shrink-0 justify-center"
                  onClick={(event) => {
                    if (resolvedSecondaryCta.onClick) {
                      resolvedSecondaryCta.onClick(event);
                      return;
                    }
                    if (resolvedSecondaryCta.href) {
                      onNavigate?.(resolvedSecondaryCta.href);
                    }
                  }}
                >
                  {resolvedSecondaryCta.label}
                </Button>
              ) : null}

              {resolvedPrimaryCta ? (
                <Button
                  variant="primary"
                  className="w-[170px] shrink-0 justify-center bg-[var(--primary,#1D4ED8)] text-[var(--primary-foreground,#FFFFFF)] hover:bg-[var(--primary-hover,#076A00)] hover:enabled:bg-[var(--primary-hover,#076A00)] hover:text-[var(--primary-soft,#DBEAFE)] hover:enabled:text-[var(--primary-soft,#DBEAFE)]"
                  onClick={(event) => {
                    if (resolvedPrimaryCta.onClick) {
                      resolvedPrimaryCta.onClick(event);
                      return;
                    }
                    if (resolvedPrimaryCta.href) {
                      onNavigate?.(resolvedPrimaryCta.href);
                    }
                  }}
                >
                  {resolvedPrimaryCta.label}
                </Button>
              ) : null}
            </div>

            <div className="flex items-center gap-2 lg:hidden">
              {mobileUserAuth ? (
                <button
                  type="button"
                  className="inline-flex min-h-11 items-center justify-center rounded-md border border-[var(--border,#E2E8F0)] bg-white px-2.5 py-2 text-[12px] font-medium text-[var(--primary,#1D4ED8)] transition-colors hover:bg-[var(--primary-soft,#DBEAFE)] hover:text-[var(--primary-hover,#1E40AF)]"
                  onClick={(event) => resolveLinkClick(event, mobileUserAuth.href, mobileUserAuth.onClick, onNavigate)}
                  aria-label={mobileUserAuth.label || 'Account'}
                >
                  {mobileUserAuth.label || 'Account'}
                </button>
              ) : null}

              <button
                type="button"
                className="inline-flex h-11 w-11 items-center justify-center rounded-md text-[#475569] transition-colors hover:bg-[#F8FAFC] hover:text-[#1D4ED8]"
                onClick={() => onNavigate?.('/directory')}
                aria-label="Open search"
              >
                <Search className="h-6 w-6" />
              </button>

              <button
                type="button"
                className="inline-flex h-11 w-11 items-center justify-center rounded-md text-[#475569] transition-colors hover:bg-[#F8FAFC] hover:text-[#1D4ED8]"
                onClick={() => setIsMobileMenuOpen((value) => !value)}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </PublicContainer>

        {isMobileMenuOpen ? (
          <div className="border-t border-[#E5E7EB] bg-white lg:hidden">
            <PublicContainer className="py-5">
              <nav className="space-y-1" aria-label="Mobile primary">
                {resolvedNavItems.map((item) => {
                  const childItems = Array.isArray(item.children) ? item.children.filter((child) => child?.label && child?.href) : [];
                  return (
                    <div key={`mobile-${item.label}`}>
                      <a
                        href={item.href || '#'}
                        className={clsx(
                          'flex min-h-11 items-center rounded-md px-3 py-2 font-body text-[15px] font-medium transition-colors',
                          item.active
                            ? 'bg-[var(--primary,#1D4ED8)] text-[var(--primary-foreground,#FFFFFF)] hover:text-[var(--primary-soft,#DBEAFE)]'
                            : 'text-[var(--primary,#1D4ED8)] hover:bg-[var(--primary-soft,#DBEAFE)] hover:text-[var(--primary-hover,#1E40AF)]'
                        )}
                        onClick={(event) => {
                          resolveLinkClick(event, item.href, item.onClick, onNavigate);
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        {item.label}
                      </a>
                      {childItems.length ? (
                        <div className="ml-4 border-l border-[#E2E8F0] pl-2">
                          {childItems.map((child) => (
                            <a
                              key={`mobile-${item.label}-${child.label}`}
                              href={child.href}
                              className="flex min-h-10 items-center rounded-md px-3 py-2 font-body text-[14px] font-medium text-[#475569] transition-colors hover:bg-[var(--primary-soft,#DBEAFE)] hover:text-[var(--primary-hover,#1E40AF)]"
                              onClick={(event) => {
                                resolveLinkClick(event, child.href, child.onClick, onNavigate);
                                setIsMobileMenuOpen(false);
                              }}
                            >
                              {child.label}
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </nav>

              {resolvedSecondaryCta ? (
                <div className="mt-4 border-t border-[#E2E8F0] pt-4">
                  <Button
                    variant="secondary"
                    size="md"
                    className="w-full"
                    onClick={(event) => {
                      setIsMobileMenuOpen(false);
                      if (resolvedSecondaryCta.onClick) {
                        resolvedSecondaryCta.onClick(event);
                        return;
                      }
                      if (resolvedSecondaryCta.href) {
                        onNavigate?.(resolvedSecondaryCta.href);
                      }
                    }}
                  >
                    {resolvedSecondaryCta.label}
                  </Button>
                </div>
              ) : null}

              {resolvedPrimaryCta ? (
                <div className="mt-4 border-t border-[#E2E8F0] pt-4">
                  <Button
                    variant="primary"
                    size="md"
                    className="w-full bg-[var(--primary,#1D4ED8)] text-[var(--primary-foreground,#FFFFFF)] hover:bg-[var(--primary-hover,#076A00)] hover:enabled:bg-[var(--primary-hover,#076A00)] hover:text-[var(--primary-soft,#DBEAFE)] hover:enabled:text-[var(--primary-soft,#DBEAFE)]"
                    onClick={(event) => {
                      setIsMobileMenuOpen(false);
                      if (resolvedPrimaryCta.onClick) {
                        resolvedPrimaryCta.onClick(event);
                        return;
                      }
                      if (resolvedPrimaryCta.href) {
                        onNavigate?.(resolvedPrimaryCta.href);
                      }
                    }}
                  >
                    {resolvedPrimaryCta.label}
                  </Button>
                </div>
              ) : null}

              {showTopBar && utilityLinks.length > 0 ? (
                <div className="mt-4 border-t border-[#E2E8F0] pt-4">
                  <div className="flex flex-wrap items-center gap-2">
                    {utilityLinks.map((link) => (
                      <a
                        key={`mobile-util-${link.label}`}
                        href={link.href || '#'}
                        className="inline-flex min-h-11 items-center rounded bg-[#F8FAFC] px-3 py-2 font-body text-[13px] text-[#334155]"
                        onClick={(event) => {
                          resolveLinkClick(event, link.href, link.onClick, onNavigate);
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}

              {showTopBar && support?.phone ? (
                <div className="mt-4 font-body text-[13px] text-[#475569]">
                  {support.phone}
                </div>
              ) : null}
            </PublicContainer>
          </div>
        ) : null}
      </div>
    </header>
  );
};

export default PublicHeader;
